import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Copy, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import QRCode from "react-qr-code";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";

// Crypto list for top-up selection
const cryptoOptions = [
  { currency: "USDT", name: "Tether" },
  { currency: "USDC", name: "USD Coin" },
  { currency: "TON", name: "Toncoin" },
  { currency: "SOL", name: "Solana" },
  { currency: "TRX", name: "TRON" },
  { currency: "BTC", name: "Bitcoin" },
  { currency: "ETH", name: "Ethereum" },
];

const networkOptions = [
  {
    id: "ton",
    coin: "USDT",
    title: "The Open Network",
    subtitle: "TON",
    icon: getCryptoIconUrl("TON"),
    labelKey: "topup.usdt_in_ton"
  },
  {
    id: "trc20",
    coin: "USDT",
    title: "Tron",
    subtitle: "TRC20",
    icon: getCryptoIconUrl("TRX"),
    labelKey: "topup.usdt_in_trc20"
  },
  {
    id: "sol",
    coin: "USDT",
    title: "Solana",
    subtitle: "SPL",
    icon: getCryptoIconUrl("SOL"),
    labelKey: "topup.usdt_in_sol"
  },
  {
    id: "bep20",
    coin: "USDT",
    title: "BNB Smart Chain",
    subtitle: "BEP20",
    icon: getCryptoIconUrl("BNB"),
    labelKey: "topup.usdt_in_bsc"
  },
  {
    id: "erc20",
    coin: "USDT",
    title: "Ethereum",
    subtitle: "ERC20",
    icon: getCryptoIconUrl("ETH"),
    labelKey: "topup.usdt_in_erc20"
  },
  {
    id: "usdc_erc20",
    coin: "USDC",
    title: "Ethereum",
    subtitle: "ERC20",
    icon: getCryptoIconUrl("USDC"),
    labelKey: "topup.usdc_in_erc20"
  },
  {
    id: "btc",
    coin: "BTC",
    title: "Bitcoin",
    subtitle: "BTC",
    icon: getCryptoIconUrl("BTC"),
    labelKey: "topup.btc_native"
  },
  {
    id: "toncoin",
    coin: "TON",
    title: "The Open Network",
    subtitle: "TON",
    icon: getCryptoIconUrl("TON"),
    labelKey: "topup.ton_native"
  },
  {
    id: "solana",
    coin: "SOL",
    title: "Solana",
    subtitle: "SOL",
    icon: getCryptoIconUrl("SOL"),
    labelKey: "topup.sol_native"
  },
  {
    id: "trx",
    coin: "TRX",
    title: "Tron",
    subtitle: "TRX",
    icon: getCryptoIconUrl("TRX"),
    labelKey: "topup.trx_native"
  },
  {
    id: "eth",
    coin: "ETH",
    title: "Ethereum",
    subtitle: "ETH",
    icon: getCryptoIconUrl("ETH"),
    labelKey: "topup.eth_native"
  }
];

const InlinePageHeader = ({ title, onBack, showBack = true }) => (
  <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
    {showBack ? (
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-8 w-8 items-center justify-center text-foreground"
        aria-label="Back"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    ) : (
      <span aria-hidden="true" className="h-8 w-8" />
    )}
    <h1 className="text-center text-[17px] font-semibold text-foreground">{title}</h1>
    <span aria-hidden="true" className="h-8 w-8" />
  </div>
);


const TopUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler, config } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [copied, setCopied] = useState(false);
  const [topupNetworksReady, setTopupNetworksReady] = useState(false);
  const [topupNetworksError, setTopupNetworksError] = useState(false);
  const [topupNetworksMap, setTopupNetworksMap] = useState({});

  const minTopupAmount = useMemo(() => {
    const val = Number(payment?.pay_amount);
    if (!Number.isFinite(val) || val <= 0) return null;
    const decimals = val < 1 ? 6 : 2;
    const factor = 10 ** decimals;
    const up = Math.ceil(val * factor) / factor;
    return up.toFixed(decimals).replace(/\.?0+$/, "");
  }, [payment?.pay_amount]);

  const topupFeeUsd = Number(config?.topup_fee_usd);

  const handleSelectCrypto = (crypto) => {
    haptic?.("light");

    if (!isCoinSupported(crypto.currency)) {
      toast.message(t("withdraw.temporarily_unavailable"));
      return;
    }
    setSelectedCrypto(crypto);
    setPayment(null);
    setNetwork(null);
  };

  const handleBack = useCallback(() => {
    haptic?.("light");
    if (payment) setPayment(null);
    else if (selectedCrypto) {
      setSelectedCrypto(null);
      setNetwork(null);
    }
    else navigate(-1);
  }, [haptic, payment, selectedCrypto, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  useEffect(() => {
    let isCancelled = false;

    const fetchTopupNetworks = async () => {
      setTopupNetworksError(false);
      try {
        const res = await api.get("/topup/networks", { timeout: 4500 });
        if (isCancelled) return;
        if (res?.data?.success && res.data.mode === "invoice") {
          navigate("/topup-invoice", { replace: true });
          return;
        }
        const rows = Array.isArray(res?.data?.networks) ? res.data.networks : [];
        const parsed = {};
        for (const row of rows) {
          const id = String(row?.id || "").toLowerCase();
          if (!id) continue;
          parsed[id] = row?.supported !== false;
        }
        if (!isCancelled) {
          setTopupNetworksMap(parsed);
          const ready = Boolean(res?.data?.success) && Object.keys(parsed).length > 0;
          setTopupNetworksReady(ready);
          setTopupNetworksError(!ready);
        }
      } catch (err) {
        console.error("Failed to fetch topup networks", err);
        if (!isCancelled) {
          setTopupNetworksError(true);
          setTopupNetworksMap({});
          setTopupNetworksReady(false);
        }
      }
    };

    fetchTopupNetworks();
    return () => {
      isCancelled = true;
    };
  }, [api, navigate]);

  const isNetworkSupported = useCallback((networkId) => {
    const net = String(networkId || "").toLowerCase();
    if (!topupNetworksReady) return false;
    if (!(net in topupNetworksMap)) return false;
    return topupNetworksMap[net] === true;
  }, [topupNetworksReady, topupNetworksMap]);

  const coinSupportByCode = useMemo(() => {
    const map = {};
    for (const c of cryptoOptions) {
      map[c.currency] = false;
    }
    for (const n of networkOptions) {
      const net = String(n.id || "").toLowerCase();
      const netSupported = topupNetworksReady && (topupNetworksMap[net] === true);
      if (netSupported) {
        map[n.coin] = true;
      }
    }
    return map;
  }, [topupNetworksMap, topupNetworksReady]);

  const isCoinSupported = useCallback((coinCode) => {
    const code = String(coinCode || "").toUpperCase();
    if (!topupNetworksReady) return false;
    return coinSupportByCode[code] === true;
  }, [topupNetworksReady, coinSupportByCode]);

  const handleCopy = async () => {
    if (!payment?.pay_address) return;
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(payment.pay_address);
      setCopied(true);
      toast.success(t("topup.copy_success"));
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error(t("topup.copy_error"));
    }
  };

  const handleCreatePayment = async (networkId) => {
    if (!isNetworkSupported(networkId)) {
      toast.message(t("withdraw.temporarily_unavailable"));
      return;
    }
    haptic?.("medium");

    setLoading(true);
    try {
      const res = await api.post("/topup/payment", {
        network: networkId, // ton | trc20 | bep20 | sol
      });
      const data = res?.data;
      if (!data?.pay_address) {
        toast.error(t("topup.address_error"));
        return;
      }
      setPayment({
        order_id: data.order_id,
        payment_id: data.payment_id,
        pay_address: data.pay_address,
        pay_amount: data.pay_amount,
        pay_currency: data.pay_currency,
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(t("common.error_auth"));
        return;
      }
      toast.error(t("topup.payment_error"));
    } finally {
      setLoading(false);
    }
  };

  const selectNetwork = (id) => {
    if (!isNetworkSupported(id)) return;
    haptic?.("light");
    setPayment(null);
    setNetwork(id);
    handleCreatePayment(id);
  };

  const availableNetworks = useMemo(() => {
    if (!selectedCrypto) return [];
    return networkOptions.filter(
      (n) => n.coin === selectedCrypto.currency && isNetworkSupported(n.id)
    );
  }, [selectedCrypto, isNetworkSupported]);

  const selectedNetwork = useMemo(
    () => availableNetworks.find((n) => n.id === network) || null,
    [network, availableNetworks]
  );
  const selectedNetworkSupported = network ? isNetworkSupported(network) : false;

  const visibleCryptos = useMemo(
    () => cryptoOptions.filter((crypto) => isCoinSupported(crypto.currency)),
    [isCoinSupported]
  );

  useEffect(() => {
    if (!selectedCrypto) return;
    if (!isCoinSupported(selectedCrypto.currency)) {
      setSelectedCrypto(null);
      setNetwork(null);
      setPayment(null);
    }
  }, [selectedCrypto, isCoinSupported]);

  useEffect(() => {
    if (!network) return;
    if (!isNetworkSupported(network)) {
      setNetwork(null);
      setPayment(null);
    }
  }, [network, isNetworkSupported]);

  // 1) Экран выбора монеты
  if (!selectedCrypto) {
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="topup-select-page">
        <InlinePageHeader title={t("topup.title")} onBack={handleBack} showBack={showInlineBack} />

        <div
          className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="p-4">
            {/* Способ пополнения: крипта (эта вкладка) / рубли (обратный QR) */}
            <div className="flex gap-1 p-1 mb-4 rounded-xl bg-[var(--lp-tile)]">
              <button
                type="button"
                aria-pressed="true"
                className="lp-press lp-tile flex-1 py-2 !rounded-lg text-sm font-semibold text-foreground"
              >
                {t("topup.method_crypto") || "Криптовалюта"}
              </button>
              <button
                type="button"
                onClick={() => { haptic?.("light"); navigate("/rub-topup"); }}
                className="flex-1 py-2 rounded-lg text-sm font-semibold text-muted-foreground"
              >
                {t("topup.method_rub") || "Рубли"}
              </button>
            </div>
            <div className="space-y-1">
              {visibleCryptos.map((crypto) => {
                return (
                  <button
                    key={crypto.currency}
                    onClick={() => handleSelectCrypto(crypto)}
                    className="w-full flex items-center p-4 rounded-xl transition-colors"
                    data-testid={`select-${crypto.currency.toLowerCase()}`}
                  >
                    <img
                      src={getCryptoIconUrl(crypto.currency)}
                      alt={crypto.currency}
                      className="w-10 h-10 rounded-full mr-3"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(crypto.currency);
                      }}
                    />
                    <div className="flex-1 text-left">
                      <div className="font-semibold flex items-center gap-2">{crypto.currency}</div>
                      <div className="text-sm text-muted-foreground">{crypto.name}</div>
                    </div>
                    <ChevronLeft className="w-5 h-5 text-gray-300 rotate-180" />
                  </button>
                );
              })}
              {visibleCryptos.length === 0 && (
                <div className="text-sm text-muted-foreground py-4">
                  {!topupNetworksReady && !topupNetworksError ? t("common.loading") : t("withdraw.temporarily_unavailable")}
                </div>
              )}
            </div>


          </div>
        </div>
      </div>
    );
            }
  // 2) Экран пополнения USDT
  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="topup-usdt-page">
      <InlinePageHeader title={t("topup.title")} onBack={handleBack} showBack={showInlineBack} />

      <div
        className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="p-6 space-y-6 relative z-20">
          {!payment && (
            <>
              {/* Network cards */}
              <div className="relative z-20">
                <label className="text-sm text-muted-foreground mb-2 block">{t('topup.network')}</label>

                <div className="space-y-1">
                  {availableNetworks.map((n) => {
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => selectNetwork(n.id)}
                        className={cn(
                          "w-full flex items-center p-4 rounded-xl transition-colors",
                          network === n.id ? "bg-primary/5 ring-1 ring-primary/30" : ""
                        )}
                        disabled={loading}
                      >
                        <img
                          src={n.icon}
                          alt={n.title}
                          className="w-10 h-10 rounded-full mr-3"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = getCryptoIconFallback(n.subtitle || n.title);
                          }}
                        />
                        <div className="flex-1 text-left">
                          <div className="font-semibold flex items-center gap-2">{n.title}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {t(n.labelKey)}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                      </button>
                    );
                  })}
                  {availableNetworks.length === 0 && (
                    <div className="text-sm text-muted-foreground py-2">
                      {!topupNetworksReady && !topupNetworksError ? t("common.loading") : t("withdraw.temporarily_unavailable")}
                    </div>
                  )}
                </div>
              </div>

              {loading && (
                <p className="text-xs text-muted-foreground text-center">{t('topup.creating_payment')}</p>
              )}

              <p className="text-xs text-muted-foreground text-center">

              </p>
            </>
          )}

          {payment && (
            <>
              {/* Blue Warning at top of address page */}
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl mb-4">
                <p className="text-sm text-blue-400 text-center">
                  {t('topup.network_warning', {
                    network: selectedNetwork?.subtitle || selectedNetwork?.title || (network || "").toUpperCase(),
                    currency: selectedCrypto?.currency || "USDT",
                  })}
                </p>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-2xl shadow-sm">
                  <QRCode value={payment.pay_address} size={200} level="H" data-testid="deposit-qr" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">{t('topup.address_label')}</label>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 flex items-center gap-3 transition-colors">
                  <p className="font-mono text-sm flex-1 break-all text-gray-900 dark:text-gray-100" data-testid="deposit-address">
                    {payment.pay_address}
                  </p>
                  <button
                    onClick={handleCopy}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      copied ? "bg-green-100 text-green-600" : "bg-white hover:bg-gray-100 text-gray-900"
                    )}
                    data-testid="copy-address-btn"
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                {(minTopupAmount || topupFeeUsd > 0) && (
                  <div className="rounded-lg border border-amber-200/80 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-700/40 px-3 py-2.5">
                    {minTopupAmount && (
                      <p className="text-xs leading-snug text-amber-900 dark:text-amber-100">
                        {t("topup.pay_amount", {
                          amount: minTopupAmount,
                          currency: (selectedCrypto?.currency || "USDT").toUpperCase(),
                        })}{" "}
                        {t("topup.pay_amount_hint")}
                      </p>
                    )}
                    {topupFeeUsd > 0 && (
                      <p className="mt-1 text-xs leading-snug text-amber-900 dark:text-amber-100">
                        {t("topup.fee_usd", { amount: topupFeeUsd.toFixed(2).replace(/\.?0+$/, "") })}
                      </p>
                    )}
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopUpPage;
